import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Today,
  ViewWeek,
  ViewDay,
  ViewModule,
  Add,
  FilterList,
  Close,
} from '@mui/icons-material';
import { eventTypeFilters, CalendarEvent } from './calendarMockData';
import { updateCalendarEvent } from '../../entities/calendar/api/calendarService';
import { useAppSelector } from '../../hooks/useAppSelector';
import { tasksApi } from '../../api/endpoints/tasksApi';
import { TaskStatus } from '../../types/common';
import './Calendar.scss';

type ViewType = 'month' | 'week' | 'day';

const Calendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>('month');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([
    'task',
    'sprint',
    'availability',
    'meeting',
    'recurring',
    'milestone',
  ]);
  const [events, setEvents] = useState<CalendarEvent[]>([]); // Initialize empty, fetch real data
  const [eventsLoading, setEventsLoading] = useState(true);
  const { user } = useAppSelector((state) => state.auth);

  React.useEffect(() => {
    const fetchRealData = async () => {
      if (user?.id) {
        try {
          const response = await tasksApi.getUserTasks(user.id.toString());
          const realEvents: CalendarEvent[] = response.tasks.map((task: any) => {
            const startDate = task.dueDate ? new Date(task.dueDate) : new Date(task.createdAt || new Date());
            // Make tasks all-day events if they just have a due date
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(startDate);
            endDate.setHours(23, 59, 59, 999);

            return {
              id: task.id,
              title: task.title,
              start: startDate,
              end: endDate,
              type: 'task',
              // Task status values from the API are lowercase (see TaskStatus
              // enum, e.g. 'done') - comparing against 'DONE' here always
              // failed, so every task rendered as in-progress regardless of
              // its real status.
              status: task.status === TaskStatus.DONE ? 'completed' : 'in-progress',
              priority: task.priority?.toLowerCase() || 'medium',
              assignee: user.name,
              description: task.description || '',
              color: '#0073ea',
              isAllDay: true,
            } as CalendarEvent;
          });
          setEvents(realEvents);
        } catch (error) {
          console.error('Failed to fetch calendar tasks', error);
        } finally {
          setEventsLoading(false);
        }
      } else {
        setEventsLoading(false);
      }
    };
    fetchRealData();
  }, [user]);

  // Edit form state
  const [editFormData, setEditFormData] = useState<Partial<CalendarEvent>>({});

  // Filter events based on active filters
  const filteredEvents = useMemo(() => {
    return events.filter((event) => activeFilters.includes(event.type));
  }, [events, activeFilters]);

  // Count active filters
  const activeFilterCount = activeFilters.length;

  // Generate calendar days for month view
  const generateMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    const current = new Date(startDate);

    while (days.length < 42) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Generate week days
  const generateWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });
  };

  // Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
    return filteredEvents.filter((event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      eventStart.setHours(0, 0, 0, 0);
      eventEnd.setHours(23, 59, 59, 999);

      return checkDate >= eventStart && checkDate <= eventEnd;
    });
  };

  // Navigation
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleFilterToggle = (filterType: string) => {
    setActiveFilters((prev) => {
      if (prev.includes(filterType)) {
        return prev.filter((f) => f !== filterType);
      } else {
        return [...prev, filterType];
      }
    });
  };

  const handleSelectAllFilters = () => {
    setActiveFilters(eventTypeFilters.map((f) => f.type));
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventDialog(true);
  };

  const handleEditClick = () => {
    if (selectedEvent) {
      // Initialize edit form with selected event data
      setEditFormData({
        title: selectedEvent.title,
        type: selectedEvent.type,
        start: selectedEvent.start,
        end: selectedEvent.end,
        description: selectedEvent.description || '',
        location: selectedEvent.location || '',
        status: selectedEvent.status,
        priority: selectedEvent.priority,
        assignee: selectedEvent.assignee,
        attendees: selectedEvent.attendees || [],
        recurrence: selectedEvent.recurrence,
        isAllDay: selectedEvent.isAllDay || false,
      });
      setShowEventDialog(false);
      setShowEditDialog(true);
    }
  };

  const handleEditFormChange = (field: keyof CalendarEvent, value: any) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedEvent) return;

    try {
      const updatedEvent = await updateCalendarEvent(selectedEvent.id, editFormData);

      // Update local events state
      setEvents((prevEvents) =>
        prevEvents.map((event) => (event.id === selectedEvent.id ? updatedEvent : event))
      );

      setShowEditDialog(false);
      setSelectedEvent(null);
      setEditFormData({});
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Failed to update event. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditDialog(false);
    setEditFormData({});
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const formatDateHeader = (): string => {
    if (viewType === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewType === 'week') {
      const weekDays = generateWeekDays();
      const start = weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${start} - ${end}, ${currentDate.getFullYear()}`;
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
  };

  const renderMonthView = () => {
    const days = generateMonthDays();
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Box className="calendar-grid">
        {/* Week day headers */}
        {weekDays.map((day) => (
          <Box key={day} className="calendar-grid__header">
            {day}
          </Box>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          const dayEvents = getEventsForDate(day);
          const isCurrentMonthDay = isCurrentMonth(day);
          const isTodayDay = isToday(day);

          return (
            <Box
              key={index}
              className={`calendar-grid__day ${!isCurrentMonthDay ? 'calendar-grid__day--other-month' : ''} ${isTodayDay ? 'calendar-grid__day--today' : ''}`}
            >
              <Box className="calendar-grid__day-number">{day.getDate()}</Box>
              <Box className="calendar-grid__events">
                {dayEvents.slice(0, 3).map((event) => (
                  <Chip
                    key={event.id}
                    label={event.title}
                    size="small"
                    onClick={() => handleEventClick(event)}
                    sx={{
                      backgroundColor: event.color,
                      color: 'white',
                      fontSize: '0.7rem',
                      height: '20px',
                      marginBottom: '2px',
                      width: '100%',
                      justifyContent: 'flex-start',
                      '& .MuiChip-label': {
                        padding: '0 4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                ))}
                {dayEvents.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>
                    +{dayEvents.length - 3} more
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekDays = generateWeekDays();
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <Box className="calendar-week">
        {/* Time column + Day headers */}
        <Box className="calendar-week__header">
          <Box className="calendar-week__time-header">Time</Box>
          {weekDays.map((day) => (
            <Box
              key={day.toISOString()}
              className={`calendar-week__day-header ${isToday(day) ? 'calendar-week__day-header--today' : ''}`}
            >
              <Typography variant="caption">{day.toLocaleDateString('en-US', { weekday: 'short' })}</Typography>
              <Typography variant="h6">{day.getDate()}</Typography>
            </Box>
          ))}
        </Box>

        {/* Time slots */}
        <Box className="calendar-week__grid">
          {hours.map((hour) => (
            <Box key={hour} className="calendar-week__row">
              <Box className="calendar-week__time">
                {hour.toString().padStart(2, '0')}:00
              </Box>
              {weekDays.map((day) => (
                <Box key={`${day.toISOString()}-${hour}`} className="calendar-week__cell">
                  {/* Events will be positioned here */}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDate(currentDate);

    return (
      <Box className="calendar-day">
        <Typography variant="h6" sx={{ mb: 2 }}>
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </Typography>
        {hours.map((hour) => {
          const hourEvents = dayEvents.filter((event) => {
            const eventHour = new Date(event.start).getHours();
            return eventHour === hour;
          });

          return (
            <Box key={hour} className="calendar-day__hour">
              <Box className="calendar-day__time">{hour.toString().padStart(2, '0')}:00</Box>
              <Box className="calendar-day__events">
                {hourEvents.map((event) => (
                  <Card
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      borderLeft: `4px solid ${event.color}`,
                      '&:hover': { boxShadow: 3 },
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="body2" fontWeight="bold">
                        {event.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(event.start).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        -
                        {new Date(event.end).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className="calendar-page">
      {eventsLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {/* Header */}
      <Box className="calendar-page__header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarToday sx={{ fontSize: 48, color: '#4A90E2' }} />
          <Box>
            <Typography variant="h4" className="calendar-page__title">
              Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage tasks, sprints, meetings, and team availability
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilterDialog(true)}
            >
              Filters
            </Button>
          </Badge>
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowCreateDialog(true)}>
            New Event
          </Button>
        </Box>
      </Box>

      {/* Calendar Controls */}
      <Box className="calendar-controls">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={handlePrevious}>
            <ChevronLeft />
          </IconButton>
          <Button variant="outlined" onClick={handleToday} startIcon={<Today />}>
            Today
          </Button>
          <IconButton onClick={handleNext}>
            <ChevronRight />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, minWidth: '250px' }}>
            {formatDateHeader()}
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={(_, newView) => newView && setViewType(newView)}
          size="small"
        >
          <ToggleButton value="month">
            <Tooltip title="Month View">
              <ViewModule />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="week">
            <Tooltip title="Week View">
              <ViewWeek />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="day">
            <Tooltip title="Day View">
              <ViewDay />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Calendar View */}
      <Card className="calendar-view">
        <CardContent>
          {viewType === 'month' && renderMonthView()}
          {viewType === 'week' && renderWeekView()}
          {viewType === 'day' && renderDayView()}
        </CardContent>
      </Card>

      {/* Filter Dialog */}
      <Dialog
        open={showFilterDialog}
        onClose={() => setShowFilterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Calendar Features Coming Soon
          </Typography>
          <IconButton onClick={() => setShowFilterDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which event types to display on your calendar
          </Typography>
          <FormGroup>
            <List sx={{ width: '100%' }}>
              {eventTypeFilters.map((filter) => (
                <ListItem
                  key={filter.type}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: activeFilters.includes(filter.type)
                          ? `${filter.color}20`
                          : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {filter.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={filter.label}
                    primaryTypographyProps={{
                      fontWeight: activeFilters.includes(filter.type) ? 500 : 400,
                      color: activeFilters.includes(filter.type) ? 'text.primary' : 'text.secondary',
                    }}
                  />
                  <Checkbox
                    edge="end"
                    checked={activeFilters.includes(filter.type)}
                    onChange={() => handleFilterToggle(filter.type)}
                    sx={{
                      color: filter.color,
                      '&.Mui-checked': {
                        color: filter.color,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </FormGroup>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            <Button onClick={handleSelectAllFilters} size="small">
              Select All
            </Button>
            <Button onClick={handleClearAllFilters} size="small">
              Clear All
            </Button>
          </Box>
          <Button variant="contained" onClick={() => setShowFilterDialog(false)}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog
        open={showEventDialog}
        onClose={() => setShowEventDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ borderLeft: `4px solid ${selectedEvent.color}` }}>
              {selectedEvent.title}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    label={selectedEvent.type.toUpperCase()}
                    size="small"
                    sx={{ backgroundColor: selectedEvent.color, color: 'white', ml: 1 }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedEvent.start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedEvent.start).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                    -{' '}
                    {new Date(selectedEvent.end).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>

                {selectedEvent.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">{selectedEvent.description}</Typography>
                  </Box>
                )}

                {selectedEvent.location && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body2">{selectedEvent.location}</Typography>
                  </Box>
                )}

                {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Attendees
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedEvent.attendees.map((attendee, index) => (
                        <Chip key={index} label={attendee} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}

                {selectedEvent.recurrence && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Recurrence
                    </Typography>
                    <Chip
                      label={`Repeats ${selectedEvent.recurrence}`}
                      size="small"
                      sx={{ ml: 1 }}
                      icon={<span>🔄</span>}
                    />
                  </Box>
                )}

                {selectedEvent.googleCalendarId && (
                  <Box>
                    <Chip
                      label="Synced with Google Calendar"
                      size="small"
                      color="primary"
                      icon={<span>📧</span>}
                    />
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowEventDialog(false)}>Close</Button>
              <Button variant="contained" color="primary" onClick={handleEditClick}>
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Event Title" fullWidth />
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select label="Event Type" defaultValue="task">
                <MenuItem value="task">Task / Milestone</MenuItem>
                <MenuItem value="sprint">Sprint Timeline</MenuItem>
                <MenuItem value="meeting">Meeting</MenuItem>
                <MenuItem value="availability">Team Availability</MenuItem>
                <MenuItem value="recurring">Recurring Event</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Start Date & Time" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="End Date & Time" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Description" multiline rows={3} fullWidth />
            <TextField label="Location" fullWidth />
            <FormControlLabel control={<Checkbox />} label="All Day Event" />
            <FormControlLabel control={<Checkbox />} label="Sync with Google Calendar" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" color="primary">
            Create Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Event Title"
              fullWidth
              value={editFormData.title || ''}
              onChange={(e) => handleEditFormChange('title', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                label="Event Type"
                value={editFormData.type || 'task'}
                onChange={(e) => handleEditFormChange('type', e.target.value)}
              >
                <MenuItem value="task">Task</MenuItem>
                <MenuItem value="milestone">Milestone</MenuItem>
                <MenuItem value="sprint">Sprint Timeline</MenuItem>
                <MenuItem value="meeting">Meeting</MenuItem>
                <MenuItem value="availability">Team Availability</MenuItem>
                <MenuItem value="recurring">Recurring Event</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={
                editFormData.start
                  ? new Date(editFormData.start).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) => handleEditFormChange('start', new Date(e.target.value))}
            />
            <TextField
              label="End Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={
                editFormData.end
                  ? new Date(editFormData.end).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) => handleEditFormChange('end', new Date(e.target.value))}
            />
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={editFormData.description || ''}
              onChange={(e) => handleEditFormChange('description', e.target.value)}
            />
            <TextField
              label="Location"
              fullWidth
              value={editFormData.location || ''}
              onChange={(e) => handleEditFormChange('location', e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editFormData.status || 'pending'}
                onChange={(e) => handleEditFormChange('status', e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={editFormData.priority || 'medium'}
                onChange={(e) => handleEditFormChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Assignee"
              fullWidth
              value={editFormData.assignee || ''}
              onChange={(e) => handleEditFormChange('assignee', e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.isAllDay || false}
                  onChange={(e) => handleEditFormChange('isAllDay', e.target.checked)}
                />
              }
              label="All Day Event"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;
